import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { CommandKeys, DEFAULT_TIMEZONE, MediaType, PlayerSettingKeys, RestartTargets, SNACK_BAR_DURATION, StatsKeys, timezonesValues } from 'src/app/shared/models/data-catalogs';
import { Player } from 'src/app/shared/models/models';
import { ErrorParserService } from 'src/app/shared/services/error-parcing.service';
import { PlayerService } from 'src/app/shared/services/player.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IPlayerService, StatData } from 'src/app/shared/models/inner-model';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';

interface ZoneAudioEngine {
  zoneId: number;
  zoneName: string;
  implLabel: string;
  isRust: boolean;
  reportedByDevice: boolean;
}

@Component({
  selector: 'app-system-view',
  templateUrl: './system-view.component.html',
  styleUrls: ['./system-view.component.scss']
})
export class SystemViewComponent implements OnInit, OnDestroy {
  timezoneForm: FormGroup;
  services: IPlayerService[] = [];
  stats: StatData[] = [];
  zoneAudioEngines: ZoneAudioEngine[] = [];
  playerSub: Subscription;
  player: Player;
  loadingServiceStats: boolean = true;
  loadingStats: boolean = true;
  isUpdateAvailable: boolean;
  unavalableToSave: boolean = false;
  /** The timezone the device still reports, when it differs from the saved one. */
  timezonePendingOn: string | null = null;

  constructor(
    private playerService: PlayerService,
    private _snackBar: MatSnackBar,
    private errorService: ErrorParserService,
    private clipboard: Clipboard,
    private formBuilder: FormBuilder,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.timezoneForm = this.formBuilder.group({
      timezone: ['', {validators: Validators.required} ]
    });

    this.playerSub = this.playerService.player$
    .pipe(
      filter((player) => !!player),
    )
    .subscribe((player) => {
      this.player = player;
      this.isUpdateAvailable = this.player.needDeviceUpdate;

      // Rebuild the list on every update so services that disappeared on the
      // device (e.g. a zone switched ZAS implementation) drop out of the view.
      const services: IPlayerService[] = [];
      this.player.deviceStats.filter((stat) => !!stat.service).forEach((stat) => {
          const service = services.find((x) => x.name === stat.service);
          if (!service) {
            services.push({
              name: stat.service,
              status: stat.key === StatsKeys.STATUS ? stat.value : '',
              created: stat.key === StatsKeys.CREATED ? stat.value : '',
              cpu: stat.key === StatsKeys.CPU ? stat.value : '',
              mem: stat.key === StatsKeys.MEM ? stat.value : ''
            });
          } else {
            service[`${stat.key}`] = stat.value;
          }
      });
      this.services = services.sort((a, b) => a.name.localeCompare(b.name));
      this.loadingServiceStats = false;

      this.zoneAudioEngines = this.buildZoneAudioEngines();

      this.syncTimezoneFromPlayer();

      this.stats = this.player.deviceStats
        .filter((stat) => !stat.service && stat.key !== StatsKeys.WG)
        .map((stat): StatData => ({ name: this.statsNameFromKey(stat.key), value: stat.value, description: stat.description}));
      this.loadingStats = false;
    });
  }

  /**
   * Determine the ZAS implementation per audio zone. Prefer what the device
   * actually reports as running (za-{id} = classic nodejs ZAS, zac-{id}/zap-{id}
   * = rust ZAS pair); fall back to the configured `zoneAudioServiceImpl`
   * player setting when no zone service is visible in the device stats.
   */
  private buildZoneAudioEngines(): ZoneAudioEngine[] {
    const audioZones = (this.player.zones || []).filter((zone) => zone.mediaType === MediaType.audio);
    const serviceNames = new Set(this.services.map(({ name }) => name));

    return audioZones.map((zone) => {
      const rustRunning = serviceNames.has(`zac-${zone.id}`) || serviceNames.has(`zap-${zone.id}`);
      const classicRunning = serviceNames.has(`za-${zone.id}`);

      let isRust: boolean;
      let reportedByDevice: boolean;
      if (rustRunning || classicRunning) {
        isRust = rustRunning;
        reportedByDevice = true;
      } else {
        const implSetting = (this.player.settings || []).find(
          ({ setting, zoneId }) => setting === PlayerSettingKeys.ZAS_IMPL && zoneId === zone.id
        );
        isRust = (implSetting?.value || '').trim().toLowerCase() === 'rust';
        reportedByDevice = false;
      }

      return {
        zoneId: zone.id,
        zoneName: zone.name,
        implLabel: isRust ? 'ZAS-RS (zac + zap)' : 'ZAS Classic (Node.js)',
        isRust,
        reportedByDevice
      };
    });
  }

  /**
   * Decide which timezone the dropdown shows, and whether the device has caught
   * up with it yet.
   *
   * Reads `configuredValue` rather than `value` deliberately. The API resolves
   * `PlayerSetting.value` to `upstreamValue || value`, so for the seconds (or
   * minutes) between an operator saving a timezone and the device reporting the
   * new one back, a read returns the OLD timezone - and the dropdown appears to
   * reset itself moments after a successful save. `configuredValue` is the
   * operator's own selection with no such fallback.
   *
   * The fallbacks still matter: a row created only by a device report carries an
   * empty `configuredValue`, and a player with no row at all has nothing but the
   * device's `timezone` stat.
   */
  private syncTimezoneFromPlayer() {
    const timezoneSetting = (this.player.settings || []).find(
      ({ setting }) => setting === PlayerSettingKeys.TIMEZONE
    );
    const configured = (timezoneSetting?.configuredValue || '').trim();
    const reported = (timezoneSetting?.upstreamValue || '').trim();
    const reportedStat = (
      this.player.deviceStats.find(({ key }) => key === StatsKeys.TIMEZONE)?.value || ''
    ).trim();

    const selected = configured || reported || reportedStat || DEFAULT_TIMEZONE;

    // Never stomp an edit in progress. `player$` re-emits on every stat, command
    // and settings update pushed by the deviceStateChanged subscription, which is
    // often enough to clear a half-made selection under the operator - a second,
    // independent way for this control to look like it reverted.
    if (this.timezoneForm.untouched) {
      this.timezoneForm.patchValue({ timezone: selected });
    }

    // Only a real disagreement is worth surfacing. A device picks the change up
    // on its next settings poll and host-service then restarts the containers to
    // apply it, so a fresh save legitimately sits here for a little while.
    this.timezonePendingOn = reported && reported !== selected ? reported : null;
  }

  get timezoneCatalog() {
    return timezonesValues;
  }

  /** Shown when the device reports docker ps Created timestamps (newer denise). */
  get showCreatedColumn(): boolean {
    return this.services.some((service) => !!service.created);
  }

  /** Shown when the device reports memory usage (newer denise). */
  get showMemColumn(): boolean {
    return this.services.some((service) => !!service.mem);
  }

  saveTimezone() {
    if (this.timezoneForm.invalid) {
      return;
    }

    this.unavalableToSave = true;
    const settingArr = [{
      setting: PlayerSettingKeys.TIMEZONE,
      value: this.timezoneForm.get('timezone').value
    }];

    this.playerService.setAllPlayerSettings(this.player.id, settingArr)
    .pipe(
      tap(() => this.timezoneForm.markAsUntouched()),
      switchMap(() => this.playerService.getPlayer(this.player.id)),
      finalize(() => this.unavalableToSave = false)
    )
    .subscribe(() => {
      this._snackBar.open(`Timezone updated successfully`, null, { duration: SNACK_BAR_DURATION });
    }, (err) => {
      const errorString = this.errorService.parseError(err);
      if (errorString) {
        this._snackBar.open(
          `Error ID: ${errorString}`,
          'Copy',
          { duration: SNACK_BAR_DURATION })
        .onAction()
        .subscribe(() => {
          this.clipboard.copy(errorString);
        });
      } else {
        this._snackBar.open(`Player updating fails`, null, { duration: SNACK_BAR_DURATION });
      }
    });
  }

  statsNameFromKey(name: string) {
    return name.replace('_', ' ');
  }

  reboot() {
    this.playerService.sendDeviceCommand(CommandKeys.REBOOT)
    .subscribe(
      () => {
        this._snackBar.open(`Player rebooted successfully`, null, { duration: SNACK_BAR_DURATION });
      },
      (err) => {
        const errorString = this.errorService.parseError(err);
        if (errorString) {
          this._snackBar.open(
            `Error ID: ${errorString}`,
            'Copy',
            { duration: SNACK_BAR_DURATION })
          .onAction()
          .subscribe(() => {
            this.clipboard.copy(errorString);
          });
        }
      });
  }

  update() {
    this.playerService.sendDeviceCommand(CommandKeys.UPDATE)
    .subscribe(
      () => {
        this._snackBar.open(`Player updated successfully`, null, { duration: SNACK_BAR_DURATION });
      },
      (err) => {
        const errorString = this.errorService.parseError(err);
        if (errorString) {
          this._snackBar.open(
            `Error ID: ${errorString}`,
            'Copy',
            { duration: SNACK_BAR_DURATION })
          .onAction()
          .subscribe(() => {
            this.clipboard.copy(errorString);
          });
        }
      });
  }

  restart(name: string, successMessage?: string) {
    this.playerService.sendDeviceCommand(CommandKeys.RESTART_SERVICE, name)
    .subscribe(
      () => {
        this._snackBar.open(successMessage || `Service ${name} restarted`, null, { duration: SNACK_BAR_DURATION });
      },
      (err) => {
        const errorString = this.errorService.parseError(err);
        if (errorString) {
          this._snackBar.open(
            `Error ID: ${errorString}`,
            'Copy',
            { duration: SNACK_BAR_DURATION })
          .onAction()
          .subscribe(() => {
            this.clipboard.copy(errorString);
          });
        }
      });
  }

  restartSafeServices() {
    this.restart(RestartTargets.ALL_SAFE, 'Restarting all non-audio services');
  }

  restartAllServices() {
    this.confirmThenRestart(
      'Restart all services?',
      'This restarts every service on the player including those delivering audio. ' +
      'Audio playback will be briefly interrupted in all zones.',
      RestartTargets.ALL_WITH_AUDIO,
      'Restarting all services (including audio)'
    );
  }

  restartDatabase() {
    this.confirmThenRestart(
      'Restart local database?',
      'This restarts the player\'s local state database (MongoDB). ' +
      'Audio playback will be interrupted while services reconnect.',
      RestartTargets.MONGO,
      'Restarting local state database'
    );
  }

  private confirmThenRestart(title: string, message: string, target: string, successMessage: string) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title,
        message,
        confirmLabel: 'Restart'
      }
    })
    .afterClosed()
    .subscribe((confirmed) => {
      if (confirmed) {
        this.restart(target, successMessage);
      }
    });
  }

  ngOnDestroy() {
    this.playerSub.unsubscribe();
  }

}
